import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../styledModals/StyledModal';
import CustomModal from '../CustomModal';
import ConnectLinkedInProfileCard, {
    LINKEDIN_CONNECT_PAGE_SUBTITLE,
} from '../../../pages/onboarding/components/ConnectLinkedInProfileCard';
import SyncNetworkCard, { SYNC_NETWORK_PAGE_SUBTITLE } from '../../../pages/onboarding/components/SyncNetworkCard';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { useAppStore } from '../../../stores/application/useAppStore';
import { isOnboardingSurveyCompleted } from '../../../pages/onboarding/onboardingSurveyUtils';
import { LINKEDIN_CONNECT_DISMISSED_SESSION_KEY, LINKEDIN_SYNC_DECLINED_SESSION_KEY, OPEN_LINKEDIN_CONNECT_MODAL_EVENT } from '../../../pages/onboarding/linkedinConnectFlowUtils';
import { isMobileDevice } from '../../../utils';
import { ILinkedInUserData, IUser } from '../../../interfaces/models/user';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { useExternalHandlers } from '../../../services/actions/navigation/useExternalHandlers';
import { ONBOARDING_CARD_WIDTH } from '../../../pages/onboarding/onboardingLayoutConstants';
import { useLinkedInProfileAutoCheck } from '../../../pages/onboarding/useLinkedInProfileAutoCheck';

const NOT_LOGGED_IN_MESSAGE =
    'Open a new tab, log into your LinkedIn account, then return here and refresh.';

const WRONG_PROFILE_MESSAGE =
    'Open a new tab, log out of your current LinkedIn account, and log in with the correct one. Then return to this page.';

const CONNECT_MIN_FEEDBACK_MS = 600;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const MODAL_WIDTH = `${ONBOARDING_CARD_WIDTH + 48}px`;

type ModalPhase = 'connect' | 'sync';

const ConnectLinkedInProfileModal: FunctionComponent = () => {
    const { store: { extensionInstalled, onboardingStep } } = useAppStore();
    const { store: { user, authData } } = useAuthStore();
    const { getLoggedInLIUserHandler, saveLinkedInDataToUser, syncConnectionsHandler, syncConversationsHandler } =
        useAuthHandlers();
    const { openPermissionHandler } = useExternalHandlers();
    const { showErrorToast } = useAppHandlers();

    const surveyCompleted = isOnboardingSurveyCompleted(user);
    const hasLinkedInProfile = Boolean(user?.linkedinData?.publicIdentifier);

    /** Auto-open only when LinkedIn profile is not connected yet — never auto-prompt sync. */
    const shouldAutoOpenConnectModal =
        Boolean(user) &&
        extensionInstalled &&
        onboardingStep < 0 &&
        surveyCompleted &&
        !hasLinkedInProfile &&
        !isMobileDevice();

    const [dismissed, setDismissed] = useState<boolean>(() => {
        try {
            return sessionStorage.getItem(LINKEDIN_CONNECT_DISMISSED_SESSION_KEY) === '1';
        } catch {
            return false;
        }
    });

    const [phase, setPhase] = useState<ModalPhase>('connect');
    const [open, setOpen] = useState(false);
    const [connectedUser, setConnectedUser] = useState<IUser | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [notCorrectProfile, setNotCorrectProfile] = useState(false);
    const [userLI, setUserLI] = useState<ILinkedInUserData | undefined>(undefined);

    const fetchInFlightRef = useRef(false);
    const prevExtensionInstalledRef = useRef(extensionInstalled);
    const flowSessionRef = useRef<'idle' | 'connect' | 'sync' | 'dismissed'>('idle');

    const resetConnectFormState = useCallback(() => {
        setConnectedUser(undefined);
        setUserLI(undefined);
        setStatusMessage('');
        setNotCorrectProfile(false);
        setConnecting(false);
        setLoading(false);
    }, []);

    const dismissModal = useCallback(() => {
        const wasSyncPhase = phase === 'sync';
        flowSessionRef.current = 'dismissed';
        setDismissed(true);
        setOpen(false);
        setPhase('connect');
        resetConnectFormState();
        try {
            sessionStorage.setItem(LINKEDIN_CONNECT_DISMISSED_SESSION_KEY, '1');
            if (wasSyncPhase) {
                sessionStorage.setItem(LINKEDIN_SYNC_DECLINED_SESSION_KEY, '1');
            }
        } catch {
            /* ignore */
        }
    }, [phase, resetConnectFormState]);

    useEffect(() => {
        const wasInstalled = prevExtensionInstalledRef.current;
        prevExtensionInstalledRef.current = extensionInstalled;

        if (!wasInstalled && extensionInstalled && shouldAutoOpenConnectModal) {
            try {
                sessionStorage.removeItem(LINKEDIN_CONNECT_DISMISSED_SESSION_KEY);
            } catch {
                /* ignore */
            }
            setDismissed(false);
            flowSessionRef.current = 'idle';
        }
    }, [shouldAutoOpenConnectModal, extensionInstalled]);

    useEffect(() => {
        if (flowSessionRef.current === 'dismissed') {
            return;
        }

        const isActiveUserFlow =
            flowSessionRef.current === 'connect' || flowSessionRef.current === 'sync';

        if (shouldAutoOpenConnectModal && !dismissed) {
            if (flowSessionRef.current === 'idle') {
                flowSessionRef.current = 'connect';
                setPhase('connect');
            }
            setOpen(true);
            return;
        }

        // Do not close while user is in connect/sync flow (e.g. connectProfile → sync phase).
        if (!shouldAutoOpenConnectModal && !isActiveUserFlow) {
            setOpen(false);
        }
    }, [shouldAutoOpenConnectModal, dismissed, phase]);

    useEffect(() => {
        const handleOpenRequest = () => {
            if (isMobileDevice()) return;
            flowSessionRef.current = hasLinkedInProfile ? 'sync' : 'connect';
            setDismissed(false);
            setPhase(hasLinkedInProfile ? 'sync' : 'connect');
            resetConnectFormState();
            setOpen(true);
        };

        window.addEventListener(OPEN_LINKEDIN_CONNECT_MODAL_EVENT, handleOpenRequest);
        return () => window.removeEventListener(OPEN_LINKEDIN_CONNECT_MODAL_EVENT, handleOpenRequest);
    }, [hasLinkedInProfile, resetConnectFormState]);

    const checkYourLIConnection = useCallback(async () => {
        if (fetchInFlightRef.current) return;

        fetchInFlightRef.current = true;
        setLoading(true);
        setNotCorrectProfile(false);
        setStatusMessage('');

        try {
            const linkedInUser = await getLoggedInLIUserHandler();

            if (
                user?.linkedinData?.publicIdentifier &&
                linkedInUser?.publicIdentifier &&
                linkedInUser.publicIdentifier !== user.linkedinData.publicIdentifier
            ) {
                setNotCorrectProfile(true);
                setStatusMessage(WRONG_PROFILE_MESSAGE);
                setUserLI(linkedInUser);
                return;
            }

            if (!linkedInUser) {
                setStatusMessage(NOT_LOGGED_IN_MESSAGE);
                setUserLI(undefined);
                return;
            }

            setUserLI(linkedInUser);
        } catch {
            showErrorToast({ message: 'Failed to check LinkedIn profile. Please refresh and try again.' });
        } finally {
            setLoading(false);
            fetchInFlightRef.current = false;
        }
    }, [getLoggedInLIUserHandler, showErrorToast, user?.linkedinData?.publicIdentifier]);

    const showProfile = Boolean(userLI) && !notCorrectProfile;
    const isSyncPhase = phase === 'sync';

    useLinkedInProfileAutoCheck({
        enabled: open && !isSyncPhase && !connecting && !notCorrectProfile,
        profileFound: showProfile,
        onCheck: checkYourLIConnection,
    });

    const connectProfile = useCallback(async () => {
        if (!userLI || connecting) return;
        setConnecting(true);
        const startedAt = Date.now();

        try {
            let serverError: string | undefined;
            const result = await saveLinkedInDataToUser(userLI, (message) => { serverError = message; });
            const savedUser = result?.user ?? result;
            if (!savedUser?._id && !savedUser?.email) {
                flowSessionRef.current = 'connect';
                setPhase('connect');
                showErrorToast({ message: serverError || 'Could not connect LinkedIn profile. Please try again.' });
                return;
            }

            if (!savedUser.linkedinData?.publicIdentifier) {
                flowSessionRef.current = 'connect';
                setPhase('connect');
                showErrorToast({ message: 'Could not connect LinkedIn profile. Please try again.' });
                return;
            }

            const remaining = CONNECT_MIN_FEEDBACK_MS - (Date.now() - startedAt);
            if (remaining > 0) {
                await wait(remaining);
            }

            if (flowSessionRef.current === 'dismissed') {
                return;
            }

            flowSessionRef.current = 'sync';
            setPhase('sync');
            setConnectedUser(savedUser);
        } catch {
            flowSessionRef.current = 'connect';
            setPhase('connect');
            showErrorToast({ message: 'Failed to connect LinkedIn profile. Please try again.' });
        } finally {
            setConnecting(false);
        }
    }, [connecting, saveLinkedInDataToUser, showErrorToast, userLI]);

    const handleSync = useCallback(() => {
        const syncUser = connectedUser ?? user;
        if (!syncUser?.linkedinData?.publicIdentifier) {
            showErrorToast({ message: 'Connect your LinkedIn profile before syncing your network.' });
            flowSessionRef.current = 'connect';
            setPhase('connect');
            resetConnectFormState();
            return;
        }

        dismissModal();
        try {
            sessionStorage.removeItem(LINKEDIN_SYNC_DECLINED_SESSION_KEY);
        } catch {
            /* ignore */
        }
        void syncConnectionsHandler(syncUser, false, authData);
        void syncConversationsHandler();
    }, [
        authData,
        connectedUser,
        dismissModal,
        resetConnectFormState,
        showErrorToast,
        syncConnectionsHandler,
        syncConversationsHandler,
        user,
    ]);

    const handleContinueWithoutSync = useCallback(() => {
        dismissModal();
    }, [dismissModal]);

    if (isMobileDevice()) return null;
    if (!open) return null;

    const isSyncPhaseRender = phase === 'sync';

    return (
        <LinkedInConnectModal show onHide={dismissModal} width={MODAL_WIDTH} backdrop="static">
            <CustomModal
                headingText={isSyncPhaseRender ? 'Sync your network' : 'Connect your LinkedIn profile'}
                descriptionText={isSyncPhaseRender ? SYNC_NETWORK_PAGE_SUBTITLE : LINKEDIN_CONNECT_PAGE_SUBTITLE}
                closeButton
                onCancelHandler={dismissModal}
                bodyPadding="0 16px 16px 16px"
            >
                {isSyncPhaseRender ? (
                    <SyncNetworkCard
                        onSync={() => void handleSync()}
                        onContinueWithoutSync={handleContinueWithoutSync}
                        onOpenPermissions={openPermissionHandler}
                        syncing={false}
                    />
                ) : (
                    <ConnectLinkedInProfileCard
                        userLI={userLI}
                        statusMessage={statusMessage}
                        loading={loading}
                        connecting={connecting}
                        showProfile={showProfile}
                        onConnect={() => void connectProfile()}
                        onRefresh={() => void checkYourLIConnection()}
                        onUseDifferentProfile={() => {
                            setNotCorrectProfile(true);
                            setStatusMessage(WRONG_PROFILE_MESSAGE);
                        }}
                    />
                )}
            </CustomModal>
        </LinkedInConnectModal>
    );
};

const LinkedInConnectModal = styled(StyledModal)`
    && .modal-header {
        padding: 16px 16px 0;
    }

    && .modal-header + div:not(.modal-body) {
        padding: 16px;
    }

    && .modal-footer {
        padding: 0;
    }
`;

export default ConnectLinkedInProfileModal;
